import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator,
} from "@/components/ui/input-otp";
import { Mail, ArrowLeft, Loader2, Heart, Store, ShoppingBag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-otp`, { email });
      if (response.data.success) { toast.success("Verification code sent!"); setStep("otp"); setCountdown(300); }
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send code"); }
    finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { toast.error("Enter the complete 6-digit code"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, otp_code: otp });
      // Decode user_type from the token
      const tokenPayload = JSON.parse(atob(response.data.access_token.split('.')[1]));
      const userType = tokenPayload.user_type || "business";
      login(response.data.access_token, response.data.email, userType);
      toast.success("Login successful!");
      navigate(userType === "market" ? "/market-dashboard" : userType === "shopper" ? "/shopper-profile" : "/dashboard");
    } catch (error) { toast.error(error.response?.data?.detail || "Invalid code"); setOtp(""); }
    finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-otp`, { email });
      if (response.data.success) { toast.success("New code sent"); setCountdown(300); setOtp(""); }
    } catch { toast.error("Failed to resend code."); }
    finally { setLoading(false); }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-20rem)]">
      {/* Left: Form */}
      <div className="flex flex-col justify-center items-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-[400px]">
          {step === "email" ? (
            <>
              <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome back</h1>
              <p className="text-gray-500 mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>Enter your email to access your dashboard</p>
              <form onSubmit={handleRequestOTP} className="space-y-6">
                <div>
                  <Label className="text-[#1a1a1a] font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input id="email" type="email" placeholder="you@business.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 pl-12 rounded-lg border-gray-200" data-testid="email-input" disabled={loading} />
                  </div>
                </div>
                <Button type="submit" className="w-full h-12 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold" disabled={loading} data-testid="send-code-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Login Code"}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Don't have an account? <Link to="/register" className="text-[#1a1a1a] font-medium hover:text-[#42D78A]">Register here</Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => { setStep("email"); setOtp(""); }} className="flex items-center gap-2 text-gray-500 hover:text-[#1a1a1a] mb-8 transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /> Back to email
              </button>
              <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Enter verification code</h1>
              <p className="text-gray-500 mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>We sent a 6-digit code to <span className="font-medium text-[#1a1a1a]">{email}</span></p>
              <div className="space-y-6">
                <div className="flex justify-center otp-input-container">
                  <InputOTP maxLength={6} value={otp} onChange={(v) => setOtp(v)} data-testid="otp-input" disabled={loading}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-12 h-14 text-xl border-gray-200" />
                      <InputOTPSlot index={1} className="w-12 h-14 text-xl border-gray-200" />
                      <InputOTPSlot index={2} className="w-12 h-14 text-xl border-gray-200" />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} className="w-12 h-14 text-xl border-gray-200" />
                      <InputOTPSlot index={4} className="w-12 h-14 text-xl border-gray-200" />
                      <InputOTPSlot index={5} className="w-12 h-14 text-xl border-gray-200" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {countdown > 0 && <p className="text-center text-gray-500 text-sm">Code expires in <span className="font-medium text-[#1a1a1a]">{formatTime(countdown)}</span></p>}
                <Button onClick={handleVerifyOTP} className="w-full h-12 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold" disabled={loading || otp.length !== 6} data-testid="verify-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Verify & Login"}
                </Button>
                <Button type="button" variant="ghost" className="w-full h-10 text-gray-500 hover:text-[#1a1a1a]" onClick={handleResendOTP} disabled={loading || countdown > 240} data-testid="resend-btn">
                  {countdown > 240 ? `Resend in ${formatTime(countdown - 240)}` : "Resend Code"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right: Graphic */}
      <div className="relative hidden overflow-hidden bg-[#eefaf4] lg:block">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(66,215,138,0.20),transparent_28%),radial-gradient(circle_at_85%_78%,rgba(255,222,126,0.22),transparent_30%)]" />
        <div className="absolute inset-0 opacity-45" style={{ backgroundImage: "linear-gradient(#d7eee3 1px, transparent 1px), linear-gradient(90deg, #d7eee3 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="absolute left-12 top-16 right-12 rounded-2xl border border-[#b9efd0] bg-white/85 p-6 shadow-2xl shadow-slate-200/80 backdrop-blur-sm">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <div className="mb-2 h-3 w-36 rounded-full bg-[#1a1a1a]" />
              <div className="h-2 w-52 rounded-full bg-slate-200" />
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d9f8e7] shadow-sm">
              <Heart className="h-6 w-6 text-[#1a1a1a]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#b9efd0] bg-[#f2fff8] p-4 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#42D78A]">
                <ShoppingBag className="h-7 w-7 text-[#1a1a1a]" />
              </div>
              <div className="mb-2 h-2 w-full rounded-full bg-slate-200" />
              <div className="h-2 w-2/3 rounded-full bg-slate-200" />
            </div>
            <div className="rounded-xl border border-[#b9d1ff] bg-[#f4f8ff] p-4 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#dbe7ff]">
                <Store className="h-7 w-7 text-[#1a1a1a]" />
              </div>
              <div className="mb-2 h-2 w-full rounded-full bg-slate-200" />
              <div className="h-2 w-3/4 rounded-full bg-slate-200" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="rounded-2xl border border-[#ffe2a3] bg-[#fff9e8] p-8 shadow-xl shadow-slate-200/80">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#42D78A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              614 Shop Local
            </p>
            <p className="text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Connecting shoppers, small businesses, and markets together.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
